import {
  ascending,
  median,
  merge,
  quantile,
  range,
  rollup,
} from 'd3-array';
import { format } from 'd3-format';
import { interpolate } from 'd3-interpolate';
import { scaleQuantize } from 'd3-scale';
import { select } from 'd3-selection';
import { area, curveCatmullRom, line } from 'd3-shape';
import { timeHour } from 'd3-time';
import { timeFormat } from 'd3-time-format';

import { glucoseAccessor, nameKey } from '../utils/dataUtils';
import { getSlowTransition, scaleY, getPathPoints } from '../utils/svgUtils';
import { alignCirclesToPoints } from './GlucoseDayGraph';
import './Dashboard.css';

export const hoursOfDay = range(0, 24);
export function getHour(item) {
  return timeFormat('%H')(timeHour.round(new Date(item.time)));
};

export function padFormat(d) {
  return format('= 03d')(d).trim().replace('−', '-');
};

export function defined(d) {
  return d.value && d.value.count > 1;
};

export function getGroupQuantiles(percentile) {
  return function(data) {
    return groupQuantiles(data, percentile);
  }
};

function leftPad(string = '', width = 5) {
  return `${' '.repeat(Math.max(width - string.length, 0))}${string}`;
}

function groupQuantiles(data, percentile = 90) {
  const glucoseValues = data.map(glucoseAccessor).sort(ascending);
  const qLow = (100 - percentile) / 200;
  const qHigh = 1 - qLow;
  return {
    count: data.length,
    median: median(glucoseValues),
    qLow: quantile(glucoseValues, qLow),
    q25: quantile(glucoseValues, 0.25),
    q75: quantile(glucoseValues, 0.75),
    qHigh: quantile(glucoseValues, qHigh),
  };
};

export function pathGenerators({ x, y }) {
  return [
    {
      name: 'percentile',
      generator: area()
        .defined(defined)
        .x(x)
        .y0(d => (y(d.value.qLow)))
        .y1(d => (y(d.value.qHigh)))
        .curve(curveCatmullRom),
    },
    {
      name: 'interquartile',
      generator: area()
        .defined(defined)
        .x(x)
        .y0(d => (y(d.value.q25)))
        .y1(d => (y(d.value.q75)))
        .curve(curveCatmullRom),
    },
    {
      name: 'median line',
      generator: line()
        .defined(defined)
        .x(x)
        .y(d => (y(d.value.median)))
        .curve(curveCatmullRom),
    },
  ];
}

export function pathTween(agpData, precision, width, height) {
  // Default to an empty path positioned to lower target
  const flatPath = `M${0},${height / 2} ${width},${height / 2}Z`;
  return function tween(d) {
    const d1 = (d.generator && agpData && d.generator(agpData)) || flatPath;
    const path0 = this;
    const path1 = path0.cloneNode();
    path1.setAttribute('d', d1);
    const n0 = path0.getTotalLength();
    const n1 = path1.getTotalLength();

    // Uniform sampling of distance based on specified precision.
    const distances = [0];
    let i = 0;
    const dt = precision / Math.max(n0, n1);

    // eslint-disable-next-line no-cond-assign
    while ((i += dt) < 1) {
      distances.push(i);
    }
    distances.push(1);

    // Compute point-interpolators at each distance.
    const points = distances.map((pointInTime) => {
      const p0 = path0.getPointAtLength(pointInTime * n0);
      const p1 = path1.getPointAtLength(pointInTime * n1);
      return interpolate([p0.x, p0.y], [p1.x, p1.y]);
    });

    if (!d.generator || !d.generator(agpData)) {
      // Animate to an empty path.
      // Do this in a little more complex way.
      return function interpolateAt(slowTransition) {
        if (slowTransition < 0.8) {
          return `M ${points.map(p => p(slowTransition * 1.25)).join('L')}`;
        }
        if (slowTransition < 1) {
          const w = (width / 2) + (1 - slowTransition) * 5;
          return `M${w},${height / 2 * slowTransition} ${w},${height / 2 * slowTransition}`;
        }
        return `M${width / 2},${height / 2} ${width / 2},${height / 2}`;
      };
    }

    return function interpolateAt(pointInTime) {
      return pointInTime < 1 ? `M ${points.map(p => p(pointInTime)).join('L')}` : d1;
    };
  };
}

function render(selection) {
  const {
    data,
    formatGlucoseValue,
    hours = hoursOfDay,
    width = 720,
    height = 350,
    locale,
    percentile,
    t,
  } = selection.datum();

  const existingData = selection.selectAll('g.graphs').data();

  if (existingData && existingData[0] && existingData[0].data === data
    && existingData[0].locale === locale
    && existingData[0].percentile === percentile) {
    // Data has not changed, let's not re-render
    return;
  }

  let firstHour = hours[0];
  const lastHour = hours[hours.length - 1];

  const merged = merge(Object.values(data));
  const hourlyNest = rollup(merged, getGroupQuantiles(percentile), getHour);

  const hourlyData = hours.map(h => ({
    key: padFormat(((firstHour > lastHour) && (h >= firstHour)) ? h - 24 : h),
    value: hourlyNest.get(padFormat(h)) || {},
  }));
  if (firstHour > lastHour) {
    firstHour += -24;
  }

  const agpData = [...hourlyData];

  // Fill the last part (hours indicate start, not end times)
  // Also, to bend the splines right, copy values before start and after end
  agpData.push(
    { key: padFormat(lastHour + 1), value: hourlyNest.get(padFormat((lastHour + 1) % 24)) || {} },
    { key: padFormat(lastHour + 2), value: hourlyNest.get(padFormat((lastHour + 2) % 24)) || {} },
    { key: padFormat(lastHour + 3), value: hourlyNest.get(padFormat((lastHour + 3) % 24)) || {} },
  );
  agpData.unshift(
    { key: padFormat(firstHour - 2), value: hourlyNest.get(padFormat((firstHour + 22) % 24)) || {} },
    { key: padFormat(firstHour - 1), value: hourlyNest.get(padFormat((firstHour + 23) % 24)) || {} },
  );

  const hourWidth = width / hours.length;
  const domain = [+agpData[0].key, +agpData[agpData.length - 1].key];

  const hourScale = scaleQuantize()
    .domain(domain)
    .range(range(-2, hours.length + 2).map(h => (h * hourWidth)));

  const x = hour => hourScale(+hour.key);
  const y = scaleY(height);
  const slowTransition = getSlowTransition();

  const graphsG = selection
    .selectAll('g.graphs')
    .data([{ data, locale, percentile }])
    .join(
      enter => enter.append('g')
        .attr('class', 'agp graphs'),
      update => update
        .classed('agp', true)
        .classed('day', false)
        .classed('hypo', false),
      exit => exit.remove(),
    );

  const localeAwareNameKey = d => `${nameKey(d)}(${locale})`

  const graphsData = pathGenerators({ x, y, percentile, t});

  const title = graphsG.selectAll('title.graph.title').data([hourlyData]);
  // console.log('Selected titles', title);

  const titleEnter = title.enter().insert('title', 'path')
    .attr('class', 'graph title');

  /*
  "key": "01",
    "value": {
      "count": 163,
      "median": 146,
      "qLow": 73.3, 
      "q25": 117.5,
      "q75": 166,
      "qHigh": 203.9
    }
  */

  titleEnter.merge(title)
    // .text(d => JSON.stringify(d, null, 2));
    .text((d) => {
      // console.log('Updating title data', d);
      return `${
        d.map(h => `${h.key}-${padFormat(+h.key + 1)}`).join(' ')
      }\n${
        d.map(h => `${leftPad(formatGlucoseValue(h.value.qHigh))}`).join(' ')
      }\n${
        d.map(h => `${leftPad(formatGlucoseValue(h.value.q75))}`).join(' ')
      }\n${
        d.map(h => `${leftPad(formatGlucoseValue(h.value.median))}`).join(' ')
      }\n${
        d.map(h => `${leftPad(formatGlucoseValue(h.value.q25))}`).join(' ')
      }\n${
        d.map(h => `${leftPad(formatGlucoseValue(h.value.qLows))}`).join(' ')
      }`;
    });

  graphsG.selectAll('path.sensor')
    .data(graphsData, localeAwareNameKey)
    .join(
      enter => {
        const entered= enter.insert('path', 'g.sensor.area')
          .attr('class', d => (`sensor ${d.name}`))
          .attr('shape-rendering', 'auto')
          .attr('d', d => d.generator(agpData) || `M${width / 2},${height / 2} ${width / 2},${height / 2}`);
        entered.append('title')
          .text(d => t(`agp.graph.titles.${d.name}`, {percentile}));
        return entered;
      },
      update => {
        update.selectAll('title')
          .text(d => t(`agp.graph.titles.${d.name}`, { percentile }));
        update
          .transition(slowTransition)
          .attrTween('d', pathTween(agpData, 4, width, height));
        return update;
      },
      exit => exit.remove(),
    );
  
  const svgElement = graphsG.node().ownerSVGElement;
  const labels = select(svgElement).select('.agp.labels');
  const labelValues = hourlyNest.get(padFormat((lastHour + 1) % 24));
  if (labelValues) {
    const medianValue = y(labelValues.median);
    const upperInterQuartileValue = Math.min(medianValue - 25, y(labelValues.q75));
    const lowerInterQuartileValue = Math.max(medianValue + 25, y(labelValues.q25));
    const upperPercentileValue = Math.min(upperInterQuartileValue - 25, y(labelValues.qHigh));
    const lowerPercentileValue = Math.max(lowerInterQuartileValue + 25, y(labelValues.qLow));

    labels.select('g.percentile.upper').attr('transform', `translate(0, ${upperPercentileValue})`);
    labels.select('g.interquartile.upper').attr('transform', `translate(0, ${upperInterQuartileValue})`);
    labels.select('g.median').attr('transform', `translate(0, ${medianValue})`);
    labels.select('g.interquartile.lower').attr('transform', `translate(0, ${lowerInterQuartileValue})`);
    labels.select('g.percentile.lower').attr('transform', `translate(0, ${lowerPercentileValue})`);
  } else {
    labels.select('g.percentile.upper').attr('transform', `translate(0, 175)`);
    labels.select('g.interquartile.upper').attr('transform', `translate(0, 200)`);
    labels.select('g.median').attr('transform', `translate(0, 225)`);
    labels.select('g.interquartile.lower').attr('transform', `translate(0, 250)`);
    labels.select('g.percentile.lower').attr('transform', `translate(0, 275)`);
  }

  const medianLine = select('path.sensor.median.line').node();
  const medianPathPoints = getPathPoints(medianLine);
  alignCirclesToPoints(graphsG, medianPathPoints, slowTransition);
}

const AGPGraph = {
  init: f => f,
  render,
  destroy: f => f,
};

export default AGPGraph;